import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  DatePicker,
  Select,
  Card,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const AdministracionProfesores = () => {
  const [profesores, setProfesores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProfesor, setEditingProfesor] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Cargar profesores
  const loadProfesores = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/profesores');
      if (response.ok) {
        const data = await response.json();
        setProfesores(data);
      } else {
        message.error('Error al cargar los profesores');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los profesores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfesores();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir modal para crear nuevo profesor
  const handleCreate = () => {
    setEditingProfesor(null);
    form.resetFields();
    form.setFieldsValue({ activo: true });
    setModalVisible(true);
  };

  // Abrir modal para editar profesor
  const handleEdit = (record) => {
    setEditingProfesor(record);
    form.setFieldsValue({
      nombre: record.pro_nombre,
      apellido: record.pro_apellido,
      fechaNacimiento: record.pro_fecha_nacimiento ? dayjs(record.pro_fecha_nacimiento) : null,
      sexo: record.pro_sexo,
      usuario: record.pro_usuario || null,
      documento: record.pro_documento || null,
      telefono: record.pro_telefono || null,
      contrasenia: undefined,
      activo: record.pro_activo,
    });
    setModalVisible(true);
  };

  // Guardar profesor (crear o actualizar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      // Convertir fecha de dayjs a string formato YYYY-MM-DD
      const dataToSend = {
        ...values,
        fechaNacimiento: values.fechaNacimiento 
          ? values.fechaNacimiento.format('YYYY-MM-DD')
          : null,
      };
      
      if (editingProfesor) {
        // Actualizar
        const response = await apiClient.put(`/profesores/${editingProfesor.pro_id}`, dataToSend);
        if (response.ok) {
          message.success('Profesor actualizado correctamente');
          setModalVisible(false);
          loadProfesores();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el profesor');
        }
      } else {
        // Crear
        const response = await apiClient.post('/profesores', dataToSend);
        if (response.ok) {
          message.success('Profesor creado correctamente');
          setModalVisible(false);
          loadProfesores();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el profesor');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        // Errores de validación del formulario
        return;
      }
      message.error('Error al guardar el profesor');
    }
  };

  // Eliminar profesor
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/profesores/${id}`);
      if (response.ok) {
        message.success('Profesor eliminado correctamente');
        loadProfesores();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el profesor');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el profesor');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'pro_nombre',
      key: 'pro_nombre',
    },
    {
      title: 'Apellido',
      dataIndex: 'pro_apellido',
      key: 'pro_apellido',
    },
    {
      title: 'Fecha de Nacimiento',
      dataIndex: 'pro_fecha_nacimiento',
      key: 'pro_fecha_nacimiento',
      width: 150,
      render: (fecha) => fecha ? new Date(fecha).toLocaleDateString('es-AR') : '-',
    },
    {
      title: 'Sexo',
      dataIndex: 'pro_sexo',
      key: 'pro_sexo',
      width: 100,
      render: (sexo) => sexo || '-',
    },
    {
      title: 'Usuario',
      dataIndex: 'pro_usuario',
      key: 'pro_usuario',
      width: 180,
      render: (usuario) => usuario || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'pro_activo',
      key: 'pro_activo',
      width: 120,
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Fecha de Creación',
      dataIndex: 'pro_fecha_creacion',
      key: 'pro_fecha_creacion',
      width: 180,
      render: (fecha) => fecha ? new Date(fecha).toLocaleString('es-AR') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este profesor?"
            onConfirm={() => handleDelete(record.pro_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>Administración de Profesores</h1>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadProfesores}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nuevo Profesor
            </Button>
          </Space>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {profesores.map(profesor => (
              <Card
                key={profesor.pro_id}
                hoverable
                style={{
                  borderLeft: '4px solid #1890ff',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                        {profesor.pro_nombre} {profesor.pro_apellido}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Fecha de Nacimiento: {profesor.pro_fecha_nacimiento ? new Date(profesor.pro_fecha_nacimiento).toLocaleDateString('es-AR') : '-'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Sexo: {profesor.pro_sexo || '-'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Fecha de Creación: {profesor.pro_fecha_creacion ? new Date(profesor.pro_fecha_creacion).toLocaleString('es-AR') : '-'}
                      </div>
                    </div>
                    <Tag color={profesor.pro_activo ? 'green' : 'red'}>
                      {profesor.pro_activo ? 'Activo' : 'Inactivo'}
                    </Tag>
                  </div>
                  <Space wrap>
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(profesor)}
                    >
                      Editar
                    </Button>
                    <Popconfirm
                      title="¿Estás seguro de eliminar este profesor?"
                      onConfirm={() => handleDelete(profesor.pro_id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                      >
                        Eliminar
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={profesores}
            loading={loading}
            rowKey="pro_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} profesores`,
            }}
          />
        )}

        {/* Modal para crear/editar */}
        <Modal
          title={editingProfesor ? 'Editar Profesor' : 'Nuevo Profesor'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: 'El nombre es requerido' },
                { max: 100, message: 'El nombre no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ingrese el nombre" />
            </Form.Item>

            <Form.Item
              name="apellido"
              label="Apellido"
              rules={[
                { required: true, message: 'El apellido es requerido' },
                { max: 100, message: 'El apellido no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ingrese el apellido" />
            </Form.Item>

            <Form.Item
              name="fechaNacimiento"
              label="Fecha de Nacimiento"
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Seleccione la fecha de nacimiento"
              />
            </Form.Item>

            <Form.Item
              name="sexo"
              label="Sexo"
            >
              <Select
                placeholder="Seleccione el sexo"
                allowClear
              >
                <Select.Option value="Masculino">Masculino</Select.Option>
                <Select.Option value="Femenino">Femenino</Select.Option>
                <Select.Option value="Otro">Otro</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="usuario"
              label="Usuario de ingreso"
              rules={[
                { max: 100, message: 'El usuario no puede exceder 100 caracteres' },
                {
                  validator: (_, value) => {
                    if (!editingProfesor && !value) {
                      return Promise.reject(new Error('El usuario es requerido para crear el acceso'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input placeholder="Usuario para ingreso y fichada en terminal" />
            </Form.Item>

            <Form.Item
              name="documento"
              label="Documento (DNI)"
              rules={[{ max: 20, message: 'Máximo 20 caracteres' }]}
            >
              <Input placeholder="Opcional, para fichar en terminal" />
            </Form.Item>

            <Form.Item
              name="telefono"
              label="Teléfono"
              rules={[{ max: 30, message: 'Máximo 30 caracteres' }]}
            >
              <Input placeholder="Opcional, para fichar en terminal" />
            </Form.Item>

            <Form.Item
              name="contrasenia"
              label={editingProfesor ? 'Nueva contraseña (opcional)' : 'Contraseña'}
              rules={[
                {
                  validator: (_, value) => {
                    if (!editingProfesor && !value) {
                      return Promise.reject(new Error('La contraseña es requerida para crear el acceso'));
                    }
                    if (value && value.length < 6) {
                      return Promise.reject(new Error('La contraseña debe tener al menos 6 caracteres'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input.Password placeholder={editingProfesor ? 'Dejar vacío para no cambiarla' : 'Ingrese contraseña'} />
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AdministracionProfesores;


